import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import {
  ChapterListItem,
  DynamicHeading,
  ContentStatusButton,
  NavigateToLocation,
} from 'components';
import {
  getMarkerPercentageTotal,
  getShouldShowPercentage,
} from './OfflineContentItemHelpers';
import {
  getDepth,
  getMarkerTitle,
} from 'components/ChapterListItem/ChapterListItemHelpers';
import { OFFLINE_CHAPTER_STATUS } from 'constants';

const useStyles = makeStyles((theme) => ({
  chapterLink: {
    color: theme.palette.greyBlue['700'],
    cursor: 'pointer',
  },
  endBlock: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
  },
  fontSize: {
    fontSize: ({ depth }) => {
      switch (depth) {
        case 1:
          return theme.typography.pxToRem(16);
        case 2:
          return theme.typography.pxToRem(14);
        case 3:
          return theme.typography.pxToRem(12);
        default:
          return theme.typography.pxToRem(12);
      }
    },
  },
  percentage: {
    color: theme.palette.greyBlue[700],
    fontWeight: ({ isTopLevelMarker }) => isTopLevelMarker && 500,
  },
  title: {
    ...theme.typography.body1,
    alignSelf: 'center',
    color: theme.palette.greyBlue[700],
    paddingLeft: ({ isTopLevelMarker, depth }) =>
      !isTopLevelMarker ? theme.spacing(1 * depth) : '',
  },
  titleContainer: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  titleWrapper: {
    alignItems: 'center',
    display: 'flex',
    padding: theme.spacing(0.5, 1),
    paddingLeft: ({ hasChildren }) =>
      hasChildren ? theme.spacing(0.5) : theme.spacing(7),
    width: '100%',
  },
}));

export const OfflineContentItem = ({
  hDepth, // dynamic heading
  marker,
  markersShouldOpen = new Set(),
  markerStatusMap = new Map(),
  onClose = () => {},
  statusButtonOnClick,
  ...props
}) => {
  // this condition only used to limit and set the default dynamic heading value
  if (!hDepth || hDepth < 3) {
    hDepth = 3;
  }
  const { bookLocation, children, index, isTopLevelMarker } = marker;
  const defaultOpen = markersShouldOpen?.has(marker);
  const depth = getDepth(marker);
  const hasChildren = Boolean(children.length);
  const status =
    markerStatusMap.get(marker) ?? OFFLINE_CHAPTER_STATUS.NOT_DOWNLOADED;
  const title = getMarkerTitle(marker);

  const classes = useStyles({
    depth,
    hasChildren,
    isTopLevelMarker,
  });

  const percentage = getShouldShowPercentage(marker) ? (
    <span className={clsx(classes.percentage, classes.fontSize, 'noTranslate')}>
      {`${(getMarkerPercentageTotal(marker) * 100).toFixed(1)}%`}
    </span>
  ) : null;

  const styledTitle = (
    <span
      className={clsx(
        classes.fontSize,
        classes.title,
        classes.titleContainer,
        'noTranslate',
      )}
    >
      {title}
    </span>
  );

  const endBlock = (
    <>
      {percentage}
      <ContentStatusButton
        onClick={() => {
          statusButtonOnClick(marker, status);
        }}
        status={status}
        ariaLabelChapterTitle={title}
      />
    </>
  );

  const chapterTitle = (
    <div className={classes.titleWrapper}>
      {bookLocation ? (
        <NavigateToLocation
          bookLocation={bookLocation}
          className={classes.chapterLink}
          id={index}
          onClick={() => onClose(bookLocation)}
        >
          <DynamicHeading hDepth={hDepth}>{styledTitle}</DynamicHeading>
        </NavigateToLocation>
      ) : (
        styledTitle
      )}
      <div className={classes.endBlock}>{endBlock}</div>
    </div>
  );

  return (
    <ChapterListItem
      data-top-level-index={marker.topLevelMarker.index}
      defaultExpanded={defaultOpen}
      id={`offline-${index}`}
      listItemId={`offline-listitem-${index}`}
      orientation="left"
      title={chapterTitle}
      ariaLabelChapterTitle={title}
      {...props}
    >
      {marker.children.length > 0
        ? marker.children.map((m) => (
            <React.Fragment key={`offline-${m.index}`}>
              <OfflineContentItem
                hDepth={hDepth + 1}
                marker={m}
                markersShouldOpen={markersShouldOpen}
                markerStatusMap={markerStatusMap}
                onClose={onClose}
                statusButtonOnClick={statusButtonOnClick}
              />
            </React.Fragment>
          ))
        : null}
    </ChapterListItem>
  );
};

OfflineContentItem.propTypes = {
  /**
   * This is the component's data contents, should have { bookLocation, children, index, isTopLevelMarker }
   */
  marker: PropTypes.object.isRequired,
  /**
   * Set that will include any markers that should default to an expanded accordion state.
   */
  markersShouldOpen: PropTypes.instanceOf(Set),
  /**
   * Map that will have the marker's current download status, based on OFFLINE_CHAPTER_STATUS constants.
   */
  markerStatusMap: PropTypes.instanceOf(Map),
  /**
   * Function to call when navigation to location closes menu
   */
  onClose: PropTypes.func,
  /**
   * Callback function that handles the onClick action for the ContentStatusButton.
   */
  statusButtonOnClick: PropTypes.func.isRequired,
};

export default OfflineContentItem;
